import { ScrollReset } from '@cigam/template/dist/components';
import MomentUtils from '@date-io/moment';
import {
  createStyles,
  jssPreset,
  makeStyles,
  StylesProvider,
  ThemeProvider,
} from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { createBrowserHistory } from 'history';
import { create } from 'jss';
import rtl from 'jss-rtl';
import { SnackbarProvider } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { Router } from 'react-router-dom';
import Auth from 'src/components/Auth';
import { runtimeConfigs } from 'src/config/runtimeConfigs';
import useSettings from 'src/hooks/useSettings';
import Routes from 'src/Routes';
import authService from 'src/services/authService';
import ConfigurationService from 'src/services/configurationService';
import { createTheme } from 'src/theme';
import axios from 'src/utils/axios';
import axiosDebug from 'src/utils/axiosDebug';

const history = createBrowserHistory();
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

const useStyles = makeStyles(() =>
  createStyles({
    '@global': {
      '*': {
        boxSizing: 'border-box',
        margin: 0,
        padding: 0,
      },
      html: {
        '-webkit-font-smoothing': 'antialiased',
        '-moz-osx-font-smoothing': 'grayscale',
        height: '100%',
        width: '100%',
      },
      body: {
        height: '100%',
        width: '100%',
      },
      '#root': {
        height: '100%',
        width: '100%',
      },
    },
  })
);

function App() {
  useStyles();

  const { settings, saveSettings } = useSettings();
  const [render, setRender] = useState(false);

  const searchEnviroment = useCallback(
    async () => {
      const enviroment = await runtimeConfigs();

      if (enviroment) {
        saveSettings({
          ...settings,
          apiUrl: enviroment.API_URL || null,
          cdnUrl: enviroment.CDN_URL || null,
          cdnKey: enviroment.CDN_KEY || 1,
          apmMonitorUrl: enviroment.APM_MONITOR_URL || null,
          themes: enviroment.THEMES || null,
          theme:
            settings.theme !== 'CIGAM'
              ? settings.theme
              : enviroment.DEFAULT_THEME,
          portalBpmUrl: enviroment.PORTAL_BPM_URL,
          activePortals: [...enviroment.ACTIVE_PORTALS] || null,
          defaultLogin: enviroment.DEFAULT_LOGIN || null,
        });

        if (process.env.NODE_ENV || process.env.NODE_ENV !== 'development') {
          axios.defaults.baseURL = `${enviroment.API_URL}/api`;
          axiosDebug.defaults.baseURL = `${enviroment.API_URL}/apm`;
        }
      }

      return enviroment;
    },
    [settings, saveSettings] // OK
  );

  useEffect(() => {
    const load = async () => {
      const enviroment = await searchEnviroment();
      if (enviroment?.API_URL && authService.isAuthenticated()) {
        ConfigurationService.loadAll();
      }
      setRender(true);
    };
    if (!render) {
      load();
    }
  }, [render, searchEnviroment]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: 'pt-BR' }} />
      <ThemeProvider theme={createTheme(settings)}>
        <StylesProvider jss={jss}>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <SnackbarProvider maxSnack={10} autoHideDuration={3000}>
              <Router history={history}>
                <Auth>
                  <ScrollReset />
                  <Routes />
                </Auth>
              </Router>
            </SnackbarProvider>
          </MuiPickersUtilsProvider>
        </StylesProvider>
      </ThemeProvider>
    </>
  );
}

export default App;
